import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import { trpc } from "./lib/trpc";
import { platformTrpc } from "./lib/platform-trpc";
import { queryClient } from "./lib/query-client";
import { router } from "./router";
import { EnsureActiveOrganization } from "./components/EnsureActiveOrganization";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import "./styles/globals.css";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";
const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_KEY) {
  // Surfaced early in dev so a missing key is obvious.
  // eslint-disable-next-line no-console -- intentional startup diagnostic (no Pino on the client)
  console.error(
    "VITE_CLERK_PUBLISHABLE_KEY is not set — authentication will not work.",
  );
}

function decodeJwtOrg(token: string): string | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const json = JSON.parse(
      atob(part.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    return (
      (json.org_id as string) ??
      (json.o as { id?: string } | undefined)?.id ??
      null
    );
  } catch {
    return null;
  }
}

async function authHeaders(which: string): Promise<Record<string, string>> {
  const token = await window.Clerk?.session?.getToken();
  // eslint-disable-next-line no-console -- temporary auth debug
  console.log(
    `[auth-debug] authHeaders(${which}) hasToken=${Boolean(token)} org=${
      token ? decodeJwtOrg(token) : "(no token)"
    }`,
  );
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      transformer: superjson,
      headers: () => authHeaders("tenant"),
    }),
  ],
});

// ENH-007: separate client for the platform path (/trpc/platform). Same auth
// header logic — `register` is public, profile reads/writes attach the token.
const platformTrpcClient = platformTrpc.createClient({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc/platform`,
      transformer: superjson,
      headers: () => authHeaders("platform"),
    }),
  ],
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

createRoot(rootEl).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_KEY ?? ""}
      afterSignOutUrl="/login"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <EnsureActiveOrganization>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <platformTrpc.Provider
            client={platformTrpcClient}
            queryClient={queryClient}
          >
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <RouterProvider router={router} />
                <Toaster richColors position="top-right" />
              </TooltipProvider>
            </QueryClientProvider>
          </platformTrpc.Provider>
        </trpc.Provider>
      </EnsureActiveOrganization>
    </ClerkProvider>
  </StrictMode>,
);
